import React from 'react';
import { toast } from 'react-toastify';

import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
} from '@mui/material';
import { StyledSubtitle, StyledTitle } from './index.styles';
import { useSupplierSurvey } from '../../hooks';
import { GhgSupplierSurvey, ReportingPeriod } from '../../../interfaces';
import { useTranslation } from 'react-i18next';
import { IntuitiveCalendarPeriod } from 'src/views/components/IntuitiveCalendar';
import {
  getRabenPeriodStart,
  periodToDate,
  timeConverter,
} from 'src/utils/timeConverter';

interface Props {
  open: boolean;
  onClose: () => void;
  invitation?: GhgSupplierSurvey | null;
  organizationId: number;
}

export default function InviteSupplierModal(props: Props) {
  const { t } = useTranslation();
  const { open, onClose, invitation, organizationId } = props;

  const _productName = invitation?.product_name || '';
  const _supplierName = invitation?.supplier_name || '';
  const _vatIdentifier = invitation?.vat_identifier || '';
  const _token = invitation?.access_token || '';

  const [productName, setProductName] = React.useState<string>(_productName);
  const [supplierName, setSupplierName] = React.useState<string>(_supplierName);
  const [vatIdentifier, setVatIdentifier] =
    React.useState<string>(_vatIdentifier);
  const [selectedDate, setSelectedDate] = React.useState<string>(
    timeConverter(getRabenPeriodStart(), ReportingPeriod.YEARLY)
  );
  const [token, setAccessToken] = React.useState<string>(_token);

  const _onClose = () => {
    setProductName('');
    setSupplierName('');
    setVatIdentifier('');
    setSelectedDate(
      timeConverter(getRabenPeriodStart(), ReportingPeriod.YEARLY)
    );
    setAccessToken('');
    onClose();
  };

  const handleProductNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setProductName(e.target.value);
  const handleSupplierNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSupplierName(e.target.value);
  const handleVatIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setVatIdentifier(e.target.value);

  const { create } = useSupplierSurvey(organizationId);

  const invitationLink = !!token
    ? `${window.location.origin}/suppliers/${token}`
    : null;

  const postInivitationLink = () =>
    create
      .mutateAsync({
        product_name: productName,
        supplier_name: supplierName,
        vat_identifier: vatIdentifier,
        reporting_period: selectedDate,
        organization_id: organizationId,
      })
      .then((res) => setAccessToken(res.data.token))
      .catch(() => {});

  const copyToClipboard = () => {
    navigator.clipboard.writeText(invitationLink as string);
    toast.success(t('ghg.scope3GoodsAndServices.toasts.linkCopied') as string);
    if (!invitation)
      toast.success(
        t('ghg.scope3GoodsAndServices.toasts.productRegistered') as string
      );
  };

  return (
    <Dialog open={open} onClose={_onClose} maxWidth="lg">
      <IconButton
        sx={{ position: 'absolute', right: '10px', top: '10px' }}
        onClick={_onClose}
      >
        <CloseIcon />
      </IconButton>
      <StyledTitle>
        {t('ghg.scope3GoodsAndServices.invitationModal.title')}
      </StyledTitle>
      <DialogContent sx={{ width: '900px' }}>
        <StyledSubtitle>
          {t('ghg.scope3GoodsAndServices.invitationModal.subtitle')}
        </StyledSubtitle>

        <br />

        <TextField
          fullWidth
          value={productName}
          onChange={handleProductNameChange}
          label={t(
            'ghg.scope3GoodsAndServices.invitationModal.inputs.name.label'
          )}
          sx={{
            m: '10px 0px',
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: '#000000',
            },
          }}
          disabled={create.isLoading || !!invitation?.product_name}
          InputProps={{
            classes: {
              notchedOutline: 'Black MuiOutlinedInput-notchedOutline',
            },
          }}
          InputLabelProps={{
            style: { color: 'black' },
          }}
        />
        <TextField
          fullWidth
          value={supplierName}
          onChange={handleSupplierNameChange}
          label={t(
            'ghg.scope3GoodsAndServices.invitationModal.inputs.company.label'
          )}
          sx={{
            m: '20px 0px',
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: '#000000',
            },
          }}
          disabled={create.isLoading || !!invitation?.supplier_name}
          InputProps={{
            classes: {
              notchedOutline: 'Black MuiOutlinedInput-notchedOutline',
            },
          }}
          InputLabelProps={{
            style: { color: 'black' },
          }}
        />
        <TextField
          fullWidth
          value={vatIdentifier}
          onChange={handleVatIdentifierChange}
          label={t(
            'ghg.scope3GoodsAndServices.invitationModal.inputs.vatIdentifier.label'
          )}
          sx={{
            m: '20px 0px',
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: '#000000',
            },
          }}
          disabled={create.isLoading || !!invitation?.vat_identifier}
          InputProps={{
            classes: {
              notchedOutline: 'Black MuiOutlinedInput-notchedOutline',
            },
          }}
          InputLabelProps={{
            style: { color: 'black' },
          }}
        />
        <IntuitiveCalendarPeriod
          reportingPeriod={ReportingPeriod.YEARLY}
          selectedDate={periodToDate(selectedDate, ReportingPeriod.YEARLY)}
          setSelectedDate={(selDate: Date) =>
            setSelectedDate(timeConverter(selDate, ReportingPeriod.YEARLY))
          }
        />

        <br />

        <Button
          sx={{ m: '10px 0px', minWidth: '200px' }}
          size="large"
          onClick={postInivitationLink}
          disabled={
            !productName ||
            !supplierName ||
            !vatIdentifier ||
            create.isLoading ||
            !!invitationLink
          }
        >
          {!invitationLink
            ? t('ghg.scope3GoodsAndServices.invitationModal.buttons.generate')
            : t('ghg.scope3GoodsAndServices.invitationModal.buttons.generated')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
